import React, { useMemo, useEffect, useRef } from 'react'
import { InstantSearch } from 'react-instantsearch-dom'
import { getAlgoliaClient } from '../../services/search/algoliaClientFactory'

/**
 * Standalone search provider for the Sound Library (EditorSoundLibraryPage).
 *
 * Owns all Algolia coupling for the sound domain:
 *   - client creation (SOUND_CLIPS app ID)
 *   - the InstantSearch root (connect* HOCs in EditorSoundLibraryPage work inside this tree)
 *   - per-category raw count queries (previously in EditorSoundLibraryPage.getTotalResultsCounts)
 *
 * Swapping the sound search engine requires editing only this file.
 *
 * Props:
 *   searchKey       {string}    Algolia search-only API key for the sound index.
 *   tagFilters      {Object|null} Per-category Algolia tagFilters map.
 *                               When provided, count queries are fired automatically.
 *   onCategoryCount {Function}  Called as (category, nbHits) when each category's
 *                               count query resolves.
 *   children        {ReactNode} EditorSoundLibraryPage content that uses connect* HOCs.
 */
function SoundSearchProviderContainer({
  searchKey, tagFilters, onCategoryCount, children,
}) {
  const onCategoryCountRef = useRef(onCategoryCount)
  onCategoryCountRef.current = onCategoryCount

  const searchClient = useMemo(() => {
    if (!searchKey) return null
    return getAlgoliaClient(
      process.env.REACT_APP_PLICKERS_ALGOLIA_SOUND_CLIPS_APP_ID,
      searchKey,
    )
  }, [searchKey])

  // Fire per-category count queries whenever tagFilters become available.
  // Each query resolves independently and reports via onCategoryCount so the
  // UI can show counts as they arrive rather than waiting for all to finish.
  useEffect(() => {
    if (!searchClient || !tagFilters) return
    const index = searchClient.initIndex('sound_clips')
    Object.keys(tagFilters).forEach((category) => {
      index.search('', { tagFilters: tagFilters[category] }).then(({ nbHits }) => {
        onCategoryCountRef.current(category, nbHits)
      })
    })
  }, [searchClient, tagFilters])

  if (!searchClient) return null

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName='sound_clips'
      stalledSearchDelay={10}
    >
      {children}
    </InstantSearch>
  )
}

export default SoundSearchProviderContainer
